import {
  setupJsonJWTHeaders,
  setupJsonHeaders,
  requestOk,
  decodeJwt,
  ApiError,
} from './api-util'
import { fetchWithTimeout } from '../util/fetch'
import { getText } from '../internal/TextLabels/text'
import http from './http-util'
import config from '../../../config'

const getErrorText = responseData => {
  if (
    responseData &&
    responseData.validationErrors &&
    responseData.validationErrors.length > 0
  ) {
    const field = responseData.validationErrors[0].fieldName

    if (field === 'country') {
      return getText('login.invalidCountryError')
    }
    if (field === 'store') {
      return getText('login.invalidStoreError')
    }
  }
  return getText('login.genericLoginError')
}

const handleApiError = response => {
  return response.json().then(data => {
    throw new ApiError('Api Error', data, response.status, getErrorText(data))
  })
}

class AuthServiceApi {
  static refreshLogin = refreshToken => () => {
    return fetchWithTimeout(
      `${config.authServiceUrl}/token/refresh`,
      {
        method: 'GET',
        headers: setupJsonJWTHeaders(refreshToken),
        mode: 'cors',
      },
      config.requestTimeout,
      'Timeout',
    )
  }

  static login = userData => {
    return fetchWithTimeout(
      `${config.authServiceUrl}/login/merchant`,
      {
        method: 'POST',
        headers: setupJsonHeaders(),
        mode: 'cors',
        body: JSON.stringify(userData),
      },
      config.requestTimeout,
      'Timeout',
    ).then(response => {
      if (requestOk(response)) {
        return response.json().then(data => {
          return decodeJwt(data)
        })
      }
      return handleApiError(response)
    })
  }

  static setStore = (storeInfo, token) => {
    const myInit = {
      method: 'POST',
      headers: setupJsonJWTHeaders(token),
      mode: 'cors',
      cache: 'default',
      credentials: 'same-origin',
      body: JSON.stringify(storeInfo),
    }

    return fetchWithTimeout(
      `${config.authServiceUrl}/token/store`,
      myInit,
      config.requestTimeout,
      'Timeout',
    ).then(response => {
      if (requestOk(response)) {
        return response.json()
      }
      return handleApiError(response)
    })
  }

  static logout = body => {
    const myHeaders = new Headers()
    myHeaders.append('Accept', 'application/json')
    myHeaders.append('Content-Type', 'application/x-www-form-urlencoded')

    return fetchWithTimeout(
      `${config.authServiceUrl}/token/revoke`,
      {
        method: 'POST',
        headers: myHeaders,
        mode: 'cors',
        cache: 'default',
        credentials: 'same-origin',
        body,
      },
      config.requestTimeout,
      'Timeout',
    )
  }

  static getChatToken = (claims, token) => {
    return http.post(
      token,
      `${config.authServiceUrl}/token/telia/chat/request`,
      JSON.stringify(claims),
    )
  }

  static resetPassword = (body, token) => {
    return http.put(token, `${config.authServiceUrl}/password/reset`, body)
  }

  static updatePassword = (passwords, token) => {
    return http.put(
      token,
      `${config.authServiceUrl}/password`,
      JSON.stringify(passwords),
    )
  }

  static forgotPassword = (email, language = null) => {
    const body = language
      ? { email, preferredLanguage: language.toUpperCase() }
      : { email }
    return fetchWithTimeout(
      `${config.authServiceUrl}/password/forgot`,
      {
        method: 'POST',
        headers: setupJsonHeaders(),
        mode: 'cors',
        body: JSON.stringify(body),
      },
      config.requestTimeout,
      'Timeout',
    ).then(response => {
      if (requestOk(response)) {
        return response
      }
      return handleApiError(response)
    })
  }

  static resetUserPassword = ({ userId, language }, token) => {
    const body = language
      ? { userId, preferredLanguage: language.toUpperCase() }
      : { userId }
    return fetchWithTimeout(
      `${config.authServiceUrl}/password/reset`,
      {
        method: 'POST',
        headers: setupJsonJWTHeaders(token),
        mode: 'cors',
        body: JSON.stringify(body),
      },
      config.requestTimeout,
      'Timeout',
    ).then(response => {
      if (requestOk(response)) {
        return response
      }
      return handleApiError(response)
    })
  }

  static getSpecificToken = (body, token) => {
    return http
      .post(
        token,
        `${config.authServiceUrl}/downgrade_token`,
        JSON.stringify(body),
      )
      .then(response => {
        return decodeJwt(response)
      })
  }
}

export default AuthServiceApi
